import React, { useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import rangeParser from "parse-numeric-range";
import "../styles/codeblock.css";
import { Copy } from "lucide-react";

interface CodeProps {
  children: {
    props: {
      className?: string;
      children: string;
      file?: string;
      highlights?: string;
    };
  };
}

const calculateLinesToHighlight = (
  raw: string
): ((index: number) => boolean) => {
  const lineNumbers = rangeParser(raw);
  if (lineNumbers) {
    return (index: number) => lineNumbers.includes(index + 1);
  }
  return () => false;
};

const copyToClipboard = async (str: string): Promise<boolean> => {
  try {
    // Modern browsers - primary method
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(str);
      return true;
    }

    // Fallback for non-secure contexts
    const textArea = document.createElement("textarea");
    textArea.value = str;

    // Avoid scrolling to bottom
    textArea.style.cssText = "position: fixed; top: 0; left: 0; opacity: 0;";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      console.error("Fallback: Copy to clipboard failed:", err);
      document.body.removeChild(textArea);
      return false;
    }
  } catch (err) {
    console.error("Copy to clipboard failed:", err);
    return false;
  }
};

const Code = ({ children }: CodeProps) => {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">(
    "idle"
  );

  const className = children.props.className || "";
  const code = children.props.children.trim();
  const language = className.replace(/language-/, "");
  const file = children.props.file;
  const highlights = calculateLinesToHighlight(children.props.highlights || "");

  const bgColor = "#0B121F";

  const handleCopy = async () => {
    const success = await copyToClipboard(code);
    setCopyStatus(success ? "copied" : "error");

    // Reset status after 2 seconds
    setTimeout(() => {
      setCopyStatus("idle");
    }, 2000);
  };

  return (
    <div
      className="pb-2"
      style={{
        background: bgColor,
        borderRadius: "0.4rem",
        marginTop: "2rem",
        marginBottom: "2rem",
        paddingLeft: "1.5rem",
      }}
    >
      <div className="flex relative">
        <div className="relative">
          <div
            className="bg-primary py-1   px-4"
            style={{
              // textTransform: "uppercase",
              paddingBottom: "4px",
              borderBottomLeftRadius: "0.3rem",
              borderBottomRightRadius: "0.3rem",
              fontSize: "0.8rem",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {language}
          </div>
        </div>

        {file && (
          <div className="text-xs text-secondary mx-4 pt-1 -mt-1 flex items-center justify-center">
            {file}
          </div>
        )}

        <div className="flex-grow" />

        <button
          onClick={handleCopy}
          className={`
            transition duration-300 rounded-tl-none rounded-br-none py-2 px-5 rounded
            ${
              copyStatus === "error"
                ? "bg-red-800 hover:bg-red-700"
                : "bg-gray-800 hover:bg-gray-600"
            }
          `}
          style={{
            border: "none",
            cursor: "pointer",
            color: "#E2E8F0",
            fontSize: "12px",
            lineHeight: "1",
          }}
        >
          {copyStatus === "idle" && (
            <div className="inline-block">
              <Copy className="h-3 w-3" />
            </div>
          )}
          {copyStatus === "copied" && "🎉 Copied!"}
          {copyStatus === "error" && "❌ Failed"}
        </button>
      </div>

      <div
        className="codeBox my-3 text-sm"
        style={{
          background: bgColor,
          borderRadius: "0.5rem",
        }}
      >
        <Highlight code={code} language={language} theme={themes.vsDark}>
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <pre
              className={className}
              style={{
                ...style,
                backgroundColor: "transparent",
                float: "left",
                minWidth: "100%",
              }}
            >
              {tokens.map((line, i) => (
                <div
                  key={i}
                  {...getLineProps({ line, key: i })}
                  style={{
                    background: highlights(i) ? "#00f5c426" : "transparent",
                    display: "block",
                  }}
                >
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
      </div>
    </div>
  );
};

export default Code;
